/**
 * Versioned contracts between the workflow service and processing providers.
 *
 * Workers never touch catalog tables. They receive a scoped job spec that names
 * storage locations by id (the worker resolves credentials from its own
 * configuration) and return structured results that the workflow records
 * through the control API's internal contract.
 */
import { z } from 'zod';
import { ShotDetectionParamsSchema } from './shots.ts';

export const CONTRACT_VERSION = 1;

export const RationalSchema = z.object({ num: z.number().int().positive(), den: z.number().int().positive() });

export const ObjectRefSchema = z.object({
  /** storage_locations.id */
  locationId: z.string().uuid(),
  /** Key relative to the location root; never an absolute host path. */
  key: z.string().min(1).refine((k) => !k.startsWith('/') && !k.split('/').includes('..'), 'key must be relative and contained'),
});
export type ObjectRef = z.infer<typeof ObjectRefSchema>;

/**
 * Non-secret storage location configuration, keyed by location id. Workers
 * resolve credentials for these locations from their own environment.
 */
export const LocationSpecsSchema = z.record(z.string(), z.object({ kind: z.enum(['fs', 's3', 'oci', 'agent']) }).passthrough());
export type LocationSpecs = z.infer<typeof LocationSpecsSchema>;

export const ProbeJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  runId: z.string(),
  source: ObjectRefSchema,
  locations: LocationSpecsSchema,
});
export type ProbeJob = z.infer<typeof ProbeJobSchema>;

export const StreamInfoSchema = z.object({
  index: z.number().int(),
  type: z.enum(['video', 'audio', 'subtitle', 'data', 'other']),
  codec: z.string().nullable(),
  profile: z.string().nullable().optional(),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional(),
  pixelFormat: z.string().nullable().optional(),
  frameRate: RationalSchema.nullable().optional(),
  averageFrameRate: RationalSchema.nullable().optional(),
  variableFrameRate: z.boolean().optional(),
  sampleRate: z.number().int().nullable().optional(),
  channels: z.number().int().nullable().optional(),
  channelLayout: z.string().nullable().optional(),
  bitRate: z.number().nullable().optional(),
  durationTicks: z.number().int().nullable().optional(),
  language: z.string().nullable().optional(),
});
export type StreamInfo = z.infer<typeof StreamInfoSchema>;

export const ProbeResultSchema = z.object({
  container: z.string(),
  containerLongName: z.string().nullable(),
  mediaKind: z.enum(['video', 'audio', 'image']),
  durationTicks: z.number().int().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
  bitRate: z.number().nullable(),
  frameRate: RationalSchema.nullable(),
  variableFrameRate: z.boolean(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  startTimecode: z.string().nullable(),
  creationTime: z.string().nullable(),
  streams: z.array(StreamInfoSchema),
  /** Interlaced sources are deinterlaced for proxies and renditions. */
  scanType: z.enum(['progressive', 'interlaced']).nullable().optional(),
  fieldOrder: z.string().nullable().optional(),
  /** Display rotation in degrees (phone footage); width/height are already the displayed size. */
  rotation: z.number().optional(),
  /** Display aspect ratio, e.g. 16:9 for anamorphic 720×576. */
  displayAspectRatio: z.string().nullable().optional(),
  bitDepth: z.number().int().nullable().optional(),
  colorTransfer: z.string().nullable().optional(),
  /** The picture carries an alpha channel (ProRes 4444, PNG, QuickTime Animation, VP9 with alpha…). */
  hasAlpha: z.boolean().optional(),
});
export type ProbeResult = z.infer<typeof ProbeResultSchema>;

/**
 * A probed picture's displayed size in square pixels (1.0.0-rc.11, shared 1.7.0): rotated media already have the
 * displayed width and height in the probe; anamorphic SD (720×576 at 16:9) is 1024 wide. Null when the probe has no size.
 */
export function probeDisplaySize(t: { width?: number | null; height?: number | null; displayAspectRatio?: string | null; rotation?: number } | null | undefined): { width: number; height: number } | null {
  if (!t?.width || !t.height) return null;
  const m = /^(\d+):(\d+)$/.exec(t.displayAspectRatio ?? '');
  if (!m || Math.abs(t.rotation ?? 0) % 180 === 90 || !Number(m[1]) || !Number(m[2])) return { width: t.width, height: t.height };
  const dar = Number(m[1]) / Number(m[2]);
  if (Math.abs(dar - t.width / t.height) / dar < 0.01) return { width: t.width, height: t.height };
  return { width: Math.round((t.height * dar) / 2) * 2, height: t.height };
}

/** Pixel formats with an alpha channel, as FFmpeg names them. */
export function pixelFormatHasAlpha(fmt: string | null | undefined): boolean {
  return Boolean(fmt) && /^(yuva|rgba|argb|abgr|bgra|gbrap|ya\d|pal8|rgba64|bgra64)/.test(fmt!);
}

/** VP9 and VP8 keep alpha in side data: it is only decoded by libvpx. */
export function needsLibvpxForAlpha(probe: ProbeResult): boolean {
  const v = probe.streams.find((s) => s.type === 'video');
  return Boolean(probe.hasAlpha && v && (v.codec === 'vp9' || v.codec === 'vp8') && !pixelFormatHasAlpha(v.pixelFormat));
}

export const DerivativeKind = z.enum(['proxy', 'thumbnail', 'sprite', 'waveform', 'rendition']);
export type DerivativeKind = z.infer<typeof DerivativeKind>;

export const DerivativeJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  runId: z.string(),
  kind: DerivativeKind,
  /** Named, validated preset, or the name of an administrator's transcode profile (see `custom`). */
  preset: z.string(),
  /** A transcode profile from Administration: FFmpeg output options checked by parseProfileArgs. */
  /** `legacy` (1.0.0-rc.2): saved before the option allowlist and not edited since; checked with the earlier denylist. */
  custom: z.object({ args: z.array(z.string()).min(1).max(200), extension: z.string().regex(/^[a-z0-9]{1,8}$/), name: z.string().max(120), legacy: z.boolean().optional() }).optional(),
  /** Use a hardware H.264/HEVC encoder when the worker has one that works (falls back to software). */
  hardware: z.boolean().optional(),
  /**
   * 1.2.1: with `hardware`, also decode on the hardware (`-hwaccel auto`) when a hardware encoder is used; frames come
   * back to system memory for the software filters. Falls back to software decoding. Never for alpha sources.
   */
  hardwareDecode: z.boolean().optional(),
  /**
   * 1.6.1, a built-in video proxy only: the workspace's bitrate cap in Mbit/s (proxy-cap.ts). Software adds
   * -maxrate/-bufsize to its CRF; the hardware encoders get the equivalent ceiling. Absent: no cap (a context recorded
   * before 1.6.1). A storage agent before 1.6.1 ignores it.
   */
  bitrateCapMbps: z.number().int().min(1).max(20).optional(),
  source: ObjectRefSchema,
  /** Deterministic destination: re-running overwrites the same object. */
  destination: ObjectRefSchema,
  probe: ProbeResultSchema,
  locations: LocationSpecsSchema,
  /**
   * 1.5.0, a sprite only: detect shots on the source (the proxy) with these settings and take the tiles at the shots
   * (shots.ts spriteTileTimes); the result's attributes carry SPRITE_SHOTS_ATTRIBUTE. Absent: the timed sprite.
   */
  shots: ShotDetectionParamsSchema.optional(),
});
export type DerivativeJob = z.infer<typeof DerivativeJobSchema>;

export const DerivativeResultSchema = z.object({
  kind: DerivativeKind,
  preset: z.string(),
  destination: ObjectRefSchema,
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  checksumSha256: z.string(),
  attributes: z.record(z.string(), z.unknown()),
});
export type DerivativeResult = z.infer<typeof DerivativeResultSchema>;

/** Errors a provider may return. `retryable: false` failures stop the run. */
export const ProviderErrorCode = z.enum([
  'INVALID_MEDIA',
  'UNSUPPORTED_MEDIA',
  'SOURCE_UNAVAILABLE',
  'DESTINATION_UNAVAILABLE',
  'TIMEOUT',
  'PROCESS_FAILED',
  /** A storage agent found a different file at a move or fetch destination and did not overwrite it (0.30.0). */
  'DEST_CONFLICT',
]);
export type ProviderErrorCode = z.infer<typeof ProviderErrorCode>;

export interface ProviderCapabilities {
  provider: string;
  version: string;
  operations: Array<'probe' | DerivativeKind>;
  presets: string[];
  acceleration: string[];
  storageLocationIds: string[];
  site: string;
  maxConcurrency: number;
}

// Task queue names live in a module of their own with no dependencies, for workflow code (1.0.0-rc.13).
import type { WorkerRole } from './task-queues.ts';
export { WORKER_ROLES, graphicsTaskQueue, mediaTaskQueue, workerTaskQueue, type WorkerRole } from './task-queues.ts';
export const WORKER_ROLE_INFO: Record<WorkerRole, { label: string; help: string; concurrency: number }> = {
  process: { label: 'Media processing', help: 'Inspection, thumbnails, filmstrips, waveforms and proxies for ingest', concurrency: 2 },
  transcode: { label: 'Transcoding and rendering', help: 'Renditions from workflows and sequence exports', concurrency: 1 },
  transfer: { label: 'Transfers and external jobs', help: 'Copies between storage and waiting on external transcoders', concurrency: 4 },
  // Each recording holds a slot for as long as it runs (a started live source: until stopped), so live work has its own workers.
  live: { label: 'Live recording', help: 'RTMP and SRT listeners and following growing files from watch folders', concurrency: 8 },
};

/** What a worker replica is running right now, reported with its heartbeat. */
export interface RunningActivity {
  activity: string;
  workflowId: string;
  taskQueue: string;
  attempt: number;
  startedAt: string;
  step: string | null;
  percent: number | null;
}

export const WORKFLOW_TASK_QUEUE = 'prism-workflow';

/** Steps of the standard ingest workflow, in order. Stable identifiers. */
export const INGEST_STEPS = [
  'register',
  'probe',
  'proxy',
  'thumbnail',
  'sprite',
  'waveform',
  /** Only in runs that check quality (1.0.0-rc.8: a watch folder or the uploads setting asked for it). */
  'qc',
  'index',
  'finalize',
] as const;
export type IngestStep = (typeof INGEST_STEPS)[number];

export const INGEST_STEP_LABELS: Record<IngestStep, string> = {
  register: 'Register source',
  probe: 'Inspect with ffprobe',
  proxy: 'Generate proxy',
  thumbnail: 'Generate poster',
  sprite: 'Shots and scrub images',
  waveform: 'Generate waveform',
  qc: 'Quality control',
  index: 'Update search',
  finalize: 'Mark ready',
};

/** Deterministic derivative keys, one per version/kind/preset. */
export function derivativeKey(workspaceId: string, assetId: string, versionId: string, kind: DerivativeKind, preset: string, ext: string): string {
  return `derivatives/${workspaceId}/${assetId}/${versionId}/${kind}-${preset}.${ext}`;
}
